import { datadogRum } from "@datadog/browser-rum";
import { useState } from "react";

import {
  ReportTemplateStatus,
  useDeleteReportTemplateMutation,
  useUpdateReportTemplateMutation,
} from "@api/graphql/types-and-hooks";
import { ReportTemplate } from "@components/TemplatesTable/TemplatesTable";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

export const useReportTemplateActions = ({ refetch }: { refetch: () => void }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const updateReportTemplate = useUpdateReportTemplateMutation();

  // Delete
  const deleteReportTemplateMutation = useDeleteReportTemplateMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const deleteSelectedTemplate = () => {
    if (!selectedTemplate) {
      console.error("No template selected");
      return;
    }

    const templateId = selectedTemplate.id;
    const templateName = selectedTemplate.name;
    datadogRum.addAction("delete-template", { templateId, templateName });

    const onSuccess = (message?: string, status?: boolean) => {
      if (status) {
        notifySuccess(message || "Template deleted successfully");
        datadogRum.addAction("delete-template-success", { templateId, templateName });
        refetch();
      } else {
        datadogRum.addError("delete-template-failed", {
          templateId,
          templateName,
          error: message,
        });
        notifyError(message || "Failed to delete template");
      }
    };

    const onError = (error: unknown) => {
      datadogRum.addError("delete-template-failed", { templateId, templateName, error });
      notifyError("Failed to delete template");
    };

    deleteReportTemplateMutation.mutate(
      { reportTemplateId: templateId },
      {
        onSuccess: (data) =>
          onSuccess(
            data.deleteReportTemplate?.message || "Template deleted successfully",
            data.deleteReportTemplate?.status || true,
          ),
        onError,
      },
    );

    setDeleteDialogOpen(false);
  };

  // Archive
  const [archiveDialogOpen, setArchiveDialogOpen] = useState<boolean>(false);

  const archiveTemplate = () => {
    if (!selectedTemplate) {
      console.error("No template selected");
      return;
    }
    const template = selectedTemplate;
    datadogRum.addAction("archive-template", { templateId: template.id, templateName: template.name });
    updateReportTemplate.mutate(
      {
        input: {
          reportTemplateId: template.id,
          status: ReportTemplateStatus.Archived,
        },
      },
      {
        onSuccess: (data) => {
          if (data.updateReportTemplate?.status) {
            datadogRum.addAction("archive-template-success", {
              templateId: template.id,
              templateName: template.name,
            });
            notifySuccess(data.updateReportTemplate?.message || "Template archived successfully");
            refetch();
          } else {
            datadogRum.addError("archive-template-failed", {
              templateId: template.id,
              templateName: template.name,
              error: data.updateReportTemplate?.message,
            });
            notifyError(data.updateReportTemplate?.message || "Failed to archive template");
          }
        },
        onError: (error) => {
          datadogRum.addError("archive-template-failed", {
            templateId: template.id,
            templateName: template.name,
            error,
          });
          notifyError("Failed to archive template");
        },
      },
    );
    setArchiveDialogOpen(false);
  };

  // Publish

  const [publishDialogOpen, setPublishDialogOpen] = useState<boolean>(false);

  const publishTemplate = (description?: string) => {
    if (!selectedTemplate) {
      console.error("No template selected");
      return;
    }
    const template = selectedTemplate;
    datadogRum.addAction("publish-template", { templateId: template.id, templateName: template.name });
    updateReportTemplate.mutate(
      {
        input: {
          reportTemplateId: template.id,
          status: ReportTemplateStatus.Published,
          description,
        },
      },
      {
        onSuccess: (data) => {
          if (data.updateReportTemplate?.status) {
            datadogRum.addAction("publish-template-success", {
              templateId: template.id,
              templateName: template.name,
            });
            notifySuccess(data.updateReportTemplate?.message || "Template published successfully");
            refetch();
          } else {
            datadogRum.addError("publish-template-failed", {
              templateId: template.id,
              templateName: template.name,
              error: data.updateReportTemplate?.message,
            });
            notifyError(data.updateReportTemplate?.message || "Failed to publish template");
          }
        },
        onError: (error) => {
          datadogRum.addError("publish-template-failed", {
            templateId: template.id,
            templateName: template.name,
            error,
          });
          notifyError("Failed to publish template");
        },
      },
    );
    setPublishDialogOpen(false);
  };

  return {
    deleteSelectedTemplate,
    deleteDialogOpen,
    setDeleteDialogOpen,
    archiveDialogOpen,
    setArchiveDialogOpen,
    publishDialogOpen,
    setPublishDialogOpen,
    archiveTemplate,
    publishTemplate,
    selectedTemplate,
    setSelectedTemplate,
  };
};
